import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import { 
  Briefcase, Plus, Search, Filter, Clock, CheckCircle, AlertCircle,
  MessageSquare, FileText, Send, Loader2, X, ExternalLink, Trash2,
  User, Calendar, ArrowUpCircle, ArrowRightCircle, ArrowDownCircle,
  FolderOpen, Link2, Upload, ChevronRight, UserPlus, Users
} from "lucide-react";

const priorityConfig = {
  high: { label: "High", color: "bg-red-100 text-red-800", icon: ArrowUpCircle },
  medium: { label: "Medium", color: "bg-yellow-100 text-yellow-800", icon: ArrowRightCircle },
  low: { label: "Low", color: "bg-green-100 text-green-800", icon: ArrowDownCircle }
};

const statusConfig = {
  open: { label: "Open", color: "bg-blue-100 text-blue-800" },
  in_progress: { label: "In Progress", color: "bg-purple-100 text-purple-800" },
  closed: { label: "Closed", color: "bg-gray-100 text-gray-800" }
};

const typeConfig = {
  consulting: { label: "Consulting Project", color: "bg-emerald-100 text-emerald-800" },
  support: { label: "Support Ticket", color: "bg-orange-100 text-orange-800" },
  strategy: { label: "Strategy Session", color: "bg-indigo-100 text-indigo-800" }
};

export const CaseManagement = ({ user, isAdmin = false }) => {
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showCaseDetail, setShowCaseDetail] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [creating, setCreating] = useState(false);
  
  // Form states
  const [caseForm, setCaseForm] = useState({
    title: "",
    description: "",
    case_type: "consulting",
    priority: "medium",
    client_id: "",
    client_name: "",
    client_email: "",
    assigned_consultant_id: "",
    assigned_consultant_name: "",
    // New: manual client entry
    manual_client_entry: false,
    add_to_community: false,
    add_to_plan: "",
  });
  
  // For consultant selection
  const [clients, setClients] = useState([]);
  const [consultants, setConsultants] = useState([]);
  
  // Interaction state
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Document state
  const [showAddDocument, setShowAddDocument] = useState(false);
  const [documentForm, setDocumentForm] = useState({
    name: "",
    url: "",
    doc_type: "link",
    description: ""
  });
  const [addingDocument, setAddingDocument] = useState(false);

  useEffect(() => {
    fetchCases();
    fetchStats();
    if (user?.role === "consultant") {
      fetchClients();
      fetchConsultants();
    }
  }, [user]);

  const fetchCases = async () => {
    try {
      const response = await authFetch(`${API}/cases`);
      if (response.ok) {
        const data = await response.json();
        setCases(data);
      }
    } catch (_err) {
      console.error("Error fetching cases:", _err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await authFetch(`${API}/cases/stats`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (_err) {
      console.error("Error fetching stats:", _err);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await authFetch(`${API}/cases/clients/list`);
      if (response.ok) {
        setClients(await response.json());
      }
    } catch (_err) {
      console.error("Error fetching clients:", _err);
    }
  };

  const fetchConsultants = async () => {
    try {
      const response = await authFetch(`${API}/cases/consultants/list`);
      if (response.ok) {
        setConsultants(await response.json());
      }
    } catch (_err) {
      console.error("Error fetching consultants:", _err);
    }
  };

  const fetchCaseDetail = async (caseId) => {
    try {
      const response = await authFetch(`${API}/cases/${caseId}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedCase(data);
        setShowCaseDetail(true);
      }
    } catch (_err) {
      toast.error("Failed to load case details");
    }
  };

  const handleCreateCase = async () => {
    if (!caseForm.title.trim()) {
      toast.error("Case title is required");
      return;
    }
    
    // Validate manual client entry
    if (caseForm.manual_client_entry) {
      if (!caseForm.client_name.trim() || !caseForm.client_email.trim()) {
        toast.error("Client name and email are required");
        return;
      }
    }
    
    setCreating(true);
    let clientId = caseForm.client_id;
    
    try {
      // If manual client entry, first create/invite the client
      if (caseForm.manual_client_entry && caseForm.client_email) {
        const createUserPayload = {
          email: caseForm.client_email.trim().toLowerCase(),
          name: caseForm.client_name.trim(),
          invite_type: "direct",
          role: caseForm.add_to_plan ? "client" : (caseForm.add_to_community ? "community_member" : "client"),
          tier: caseForm.add_to_plan || null
        };
        
        try {
          const userRes = await authFetch(`${API}/consultant/invite-user`, {
            method: "POST",
            body: JSON.stringify(createUserPayload)
          });
          
          if (userRes.ok) {
            const userData = await userRes.json();
            clientId = userData.user_id;
            toast.success(`Client account created for ${caseForm.client_email}`);
          } else {
            const errorData = await userRes.json();
            // If user already exists, just continue with case creation
            if (errorData.detail?.includes("already exists")) {
              toast.info("Client already exists - creating case without new account");
            } else {
              toast.error(errorData.detail || "Failed to create client account");
              setCreating(false);
              return;
            }
          }
        } catch (userErr) {
          console.error("User creation error:", userErr);
          toast.info("Could not create client account - proceeding with case only");
        }
      }
      
      const response = await authFetch(`${API}/cases`, {
        method: "POST",
        body: JSON.stringify({
          title: caseForm.title,
          description: caseForm.description,
          case_type: caseForm.case_type,
          priority: caseForm.priority,
          client_id: clientId || "",
          client_name: caseForm.client_name,
          client_email: caseForm.client_email,
          assigned_consultant_id: caseForm.assigned_consultant_id,
          assigned_consultant_name: caseForm.assigned_consultant_name
        })
      });
      
      if (response.ok) {
        toast.success("Case created successfully");
        setShowCreateDialog(false);
        setCaseForm({
          title: "", description: "", case_type: "consulting", priority: "medium",
          client_id: "", client_name: "", client_email: "",
          assigned_consultant_id: "", assigned_consultant_name: "",
          manual_client_entry: false, add_to_community: false, add_to_plan: ""
        });
        fetchCases();
        fetchStats();
        fetchClients();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create case");
      }
    } catch (_err) {
      console.error("Case creation error:", _err);
      console.error("Error name:", _err.name);
      console.error("Error message:", _err.message);
      toast.error(`Unable to connect: ${_err.message || "Network error"}`);
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateStatus = async (caseId, newStatus) => {
    try {
      const response = await authFetch(`${API}/cases/${caseId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        toast.success("Status updated");
        fetchCases();
        fetchStats();
        if (selectedCase?.case_id === caseId) {
          fetchCaseDetail(caseId);
        }
      }
    } catch (_err) {
      toast.error("Failed to update status");
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedCase) return;
    
    setSendingMessage(true);
    try {
      const response = await authFetch(`${API}/cases/${selectedCase.case_id}/interactions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: newMessage })
      });
      
      if (response.ok) {
        setNewMessage("");
        fetchCaseDetail(selectedCase.case_id);
      }
    } catch (_err) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleAddDocument = async () => {
    if (!documentForm.name.trim() || !documentForm.url.trim()) {
      toast.error("Document name and URL are required");
      return;
    }
    
    setAddingDocument(true);
    try {
      const response = await authFetch(`${API}/cases/${selectedCase.case_id}/documents`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(documentForm)
      });
      
      if (response.ok) {
        toast.success("Document added");
        setShowAddDocument(false);
        setDocumentForm({ name: "", url: "", doc_type: "link", description: "" });
        fetchCaseDetail(selectedCase.case_id);
      }
    } catch (_err) {
      toast.error("Failed to add document");
    } finally {
      setAddingDocument(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      const response = await authFetch(`${API}/cases/${selectedCase.case_id}/documents/${documentId}`, {
        method: "DELETE"
      });
      
      if (response.ok) {
        toast.success("Document deleted");
        fetchCaseDetail(selectedCase.case_id);
      }
    } catch (_err) {
      toast.error("Failed to delete document");
    }
  };

  const handleDeleteCase = async (caseId) => {
    if (!confirm("Are you sure you want to delete this case? This action cannot be undone.")) {
      return;
    }
    
    try {
      const response = await authFetch(`${API}/cases/${caseId}`, {
        method: "DELETE"
      });
      
      if (response.ok) {
        toast.success("Case deleted");
        setShowCaseDetail(false);
        setSelectedCase(null);
        fetchCases();
        fetchStats();
      }
    } catch (_err) {
      toast.error("Failed to delete case");
    }
  };

  // Filter cases
  const filteredCases = cases.filter(c => {
    const matchesSearch = c.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          c.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          c.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === "all" || c.status === filterStatus;
    const matchesType = filterType === "all" || c.case_type === filterType;
    const matchesPriority = filterPriority === "all" || c.priority === filterPriority;
    return matchesSearch && matchesStatus && matchesType && matchesPriority;
  });

  const isConsultant = user?.role === "consultant";

  return (
    <div className="space-y-6">
      {/* Stats Cards - Only for consultants */}
      {isConsultant && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Open Cases</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.open}</p>
                </div>
                <FolderOpen className="w-8 h-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">In Progress</p>
                  <p className="text-2xl font-bold text-purple-600">{stats.in_progress}</p>
                </div>
                <Clock className="w-8 h-8 text-purple-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">High Priority</p>
                  <p className="text-2xl font-bold text-red-600">{stats.high_priority}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-red-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Closed</p>
                  <p className="text-2xl font-bold text-gray-600">{stats.closed}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-gray-200" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Actions & Filters */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex flex-1 gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search cases..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="consulting">Consulting</SelectItem>
              <SelectItem value="support">Support</SelectItem>
              <SelectItem value="strategy">Strategy</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterPriority} onValueChange={setFilterPriority}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priority</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {isConsultant && (
          <Button onClick={() => setShowCreateDialog(true)} className="bg-green-600 hover:bg-green-700">
            <Plus className="w-4 h-4 mr-2" />
            New Case
          </Button>
        )}
      </div>

      {/* Cases List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : filteredCases.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Briefcase className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No cases found</p>
            {isConsultant && (
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setShowCreateDialog(true)}
              >
                Create your first case
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredCases.map((c) => {
            const PriorityIcon = priorityConfig[c.priority]?.icon || ArrowRightCircle;
            return (
              <Card 
                key={c.case_id} 
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => fetchCaseDetail(c.case_id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-semibold text-gray-900 truncate">{c.title}</h3>
                        <Badge className={statusConfig[c.status]?.color}>
                          {statusConfig[c.status]?.label}
                        </Badge>
                        <Badge variant="outline" className={typeConfig[c.case_type]?.color}>
                          {typeConfig[c.case_type]?.label}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500 line-clamp-1">{c.description}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {c.client_name || "No client"}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" />
                          {c.interaction_count || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          {c.document_count || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(c.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <PriorityIcon className={`w-5 h-5 ${
                        c.priority === "high" ? "text-red-500" : 
                        c.priority === "medium" ? "text-yellow-500" : "text-green-500"
                      }`} />
                      <ChevronRight className="w-5 h-5 text-gray-300" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Case Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Briefcase className="w-5 h-5 mr-2 text-green-600" />
              Create New Case
            </DialogTitle>
            <DialogDescription>
              Create a new case for a client
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>Case Title *</Label>
              <Input
                value={caseForm.title}
                onChange={(e) => setCaseForm({ ...caseForm, title: e.target.value })}
                placeholder="Enter case title"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={caseForm.description}
                onChange={(e) => setCaseForm({ ...caseForm, description: e.target.value })}
                placeholder="Describe the case..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Case Type</Label>
                <Select 
                  value={caseForm.case_type} 
                  onValueChange={(v) => setCaseForm({ ...caseForm, case_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="consulting">Consulting Project</SelectItem>
                    <SelectItem value="support">Support Ticket</SelectItem>
                    <SelectItem value="strategy">Strategy Session</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select 
                  value={caseForm.priority} 
                  onValueChange={(v) => setCaseForm({ ...caseForm, priority: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Client</Label>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="manual-entry"
                    checked={caseForm.manual_client_entry}
                    onCheckedChange={(checked) => setCaseForm({ 
                      ...caseForm, 
                      manual_client_entry: checked,
                      client_id: "",
                      client_name: "",
                      client_email: ""
                    })}
                  />
                  <label htmlFor="manual-entry" className="text-sm text-gray-600 cursor-pointer">
                    Add new client manually
                  </label>
                </div>
              </div>
              
              {caseForm.manual_client_entry ? (
                <div className="space-y-3 p-3 bg-gray-50 rounded-lg border border-dashed">
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                    <UserPlus className="w-4 h-4" />
                    <span>Enter new client details</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Client Name *</Label>
                      <Input
                        value={caseForm.client_name}
                        onChange={(e) => setCaseForm({ ...caseForm, client_name: e.target.value })}
                        placeholder="Full name"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Client Email *</Label>
                      <Input
                        type="email"
                        value={caseForm.client_email}
                        onChange={(e) => setCaseForm({ ...caseForm, client_email: e.target.value })}
                        placeholder="email@example.com"
                      />
                    </div>
                  </div>
                  
                  <div className="pt-2 border-t border-gray-200 space-y-2">
                    <p className="text-xs font-medium text-gray-600 flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      Also add this client to:
                    </p>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="add-community"
                          checked={caseForm.add_to_community}
                          onCheckedChange={(checked) => setCaseForm({ ...caseForm, add_to_community: checked })}
                        />
                        <label htmlFor="add-community" className="text-sm cursor-pointer">Community</label>
                      </div>
                    </div>
                    
                    <div className="space-y-1">
                      <Label className="text-xs">Add to Plan (optional)</Label>
                      <Select 
                        value={caseForm.add_to_plan || "none"} 
                        onValueChange={(v) => setCaseForm({ ...caseForm, add_to_plan: v === "none" ? "" : v })}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="No plan - case only" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No plan - case only</SelectItem>
                          <SelectItem value="basic">Basic Consulting</SelectItem>
                          <SelectItem value="premium_rapid">R.A.P.I.D.™ Program</SelectItem>
                          <SelectItem value="growth_strategic">Strategic Foundation</SelectItem>
                          <SelectItem value="growth_optimization">Growth & Optimization</SelectItem>
                          <SelectItem value="growth_scale">Scale & Leadership</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ) : (
                <Select 
                  value={caseForm.client_id} 
                  onValueChange={(v) => {
                    const client = clients.find(c => c.user_id === v);
                    setCaseForm({ 
                      ...caseForm, 
                      client_id: v,
                      client_name: client?.name || "",
                      client_email: client?.email || ""
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.user_id} value={client.user_id}>
                        {client.name} ({client.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            {isAdmin && consultants.length > 1 && (
              <div className="space-y-2">
                <Label>Assign To</Label>
                <Select 
                  value={caseForm.assigned_consultant_id} 
                  onValueChange={(v) => {
                    const consultant = consultants.find(c => c.user_id === v);
                    setCaseForm({ 
                      ...caseForm, 
                      assigned_consultant_id: v,
                      assigned_consultant_name: consultant?.name || ""
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select consultant" />
                  </SelectTrigger>
                  <SelectContent>
                    {consultants.map((consultant) => (
                      <SelectItem key={consultant.user_id} value={consultant.user_id}>
                        {consultant.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateCase} 
              disabled={creating || !caseForm.title.trim()}
              className="bg-green-600 hover:bg-green-700"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Case
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Case Detail Dialog */}
      <Dialog open={showCaseDetail} onOpenChange={(open) => {
        setShowCaseDetail(open);
        if (!open) setSelectedCase(null);
      }}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {selectedCase && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <DialogTitle className="flex items-center gap-2 flex-wrap">
                      {selectedCase.title}
                      <Badge className={statusConfig[selectedCase.status]?.color}>
                        {statusConfig[selectedCase.status]?.label}
                      </Badge>
                    </DialogTitle>
                    <DialogDescription className="flex items-center gap-4 mt-1">
                      <span>{typeConfig[selectedCase.case_type]?.label}</span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        {priorityConfig[selectedCase.priority]?.icon && 
                          <span className={`inline-flex ${
                            selectedCase.priority === "high" ? "text-red-500" : 
                            selectedCase.priority === "medium" ? "text-yellow-500" : "text-green-500"
                          }`}>
                            {(() => {
                              const Icon = priorityConfig[selectedCase.priority].icon;
                              return <Icon className="w-4 h-4" />;
                            })()}
                          </span>
                        }
                        {priorityConfig[selectedCase.priority]?.label} Priority
                      </span>
                    </DialogDescription>
                  </div>
                  {isConsultant && (
                    <div className="flex items-center gap-2">
                      <Select 
                        value={selectedCase.status} 
                        onValueChange={(v) => handleUpdateStatus(selectedCase.case_id, v)}
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                      {isAdmin && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDeleteCase(selectedCase.case_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </DialogHeader>

              <Tabs defaultValue="details" className="flex-1 overflow-hidden flex flex-col">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="interactions">
                    Interactions ({selectedCase.interactions?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="documents">
                    Documents ({selectedCase.documents?.length || 0})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="flex-1 overflow-y-auto space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Client</Label>
                      <p className="font-medium">{selectedCase.client_name || "Not assigned"}</p>
                      {selectedCase.client_email && (
                        <p className="text-sm text-gray-500">{selectedCase.client_email}</p>
                      )}
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Assigned Consultant</Label>
                      <p className="font-medium">{selectedCase.assigned_consultant_name || "Not assigned"}</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Created</Label>
                      <p className="font-medium">
                        {new Date(selectedCase.created_at).toLocaleString()}
                      </p>
                      <p className="text-sm text-gray-500">by {selectedCase.created_by_name}</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Last Updated</Label>
                      <p className="font-medium">
                        {new Date(selectedCase.updated_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-500">Description</Label>
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {selectedCase.description || "No description provided"}
                    </p>
                  </div>
                </TabsContent>

                <TabsContent value="interactions" className="flex-1 overflow-hidden flex flex-col">
                  <div className="flex-1 overflow-y-auto space-y-3 mb-4 max-h-[300px]">
                    {selectedCase.interactions?.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">No interactions yet</p>
                    ) : (
                      selectedCase.interactions?.map((interaction) => (
                        <div 
                          key={interaction.interaction_id}
                          className={`p-3 rounded-lg ${
                            interaction.interaction_type === "system" 
                              ? "bg-gray-50 text-gray-600 text-sm italic"
                              : interaction.user_role === "consultant"
                              ? "bg-green-50 border-l-4 border-green-500"
                              : "bg-blue-50 border-l-4 border-blue-500"
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-sm">
                              {interaction.user_name}
                              <Badge variant="outline" className="ml-2 text-xs">
                                {interaction.user_role}
                              </Badge>
                            </span>
                            <span className="text-xs text-gray-400">
                              {new Date(interaction.created_at).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm">{interaction.message}</p>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="flex gap-2 border-t pt-4">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSendMessage()}
                    />
                    <Button 
                      onClick={handleSendMessage}
                      disabled={sendingMessage || !newMessage.trim()}
                    >
                      {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="documents" className="flex-1 overflow-y-auto">
                  <div className="flex justify-end mb-4">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowAddDocument(true)}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Document
                    </Button>
                  </div>
                  {selectedCase.documents?.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No documents attached</p>
                  ) : (
                    <div className="space-y-2">
                      {selectedCase.documents?.map((doc) => (
                        <div 
                          key={doc.document_id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            {doc.doc_type === "link" ? (
                              <Link2 className="w-5 h-5 text-blue-500" />
                            ) : (
                              <FileText className="w-5 h-5 text-gray-500" />
                            )}
                            <div>
                              <a 
                                href={doc.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="font-medium text-blue-600 hover:underline flex items-center gap-1"
                              >
                                {doc.name}
                                <ExternalLink className="w-3 h-3" />
                              </a>
                              {doc.description && (
                                <p className="text-xs text-gray-500">{doc.description}</p>
                              )}
                              <p className="text-xs text-gray-400">
                                Added by {doc.uploaded_by_name} on {new Date(doc.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleDeleteDocument(doc.document_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add Document Dialog */}
                  <Dialog open={showAddDocument} onOpenChange={setShowAddDocument}>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>Add Document</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Document Name *</Label>
                          <Input
                            value={documentForm.name}
                            onChange={(e) => setDocumentForm({ ...documentForm, name: e.target.value })}
                            placeholder="e.g., Business Plan Draft"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>URL *</Label>
                          <Input
                            value={documentForm.url}
                            onChange={(e) => setDocumentForm({ ...documentForm, url: e.target.value })}
                            placeholder="https://..."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Type</Label>
                          <Select 
                            value={documentForm.doc_type} 
                            onValueChange={(v) => setDocumentForm({ ...documentForm, doc_type: v })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="link">External Link</SelectItem>
                              <SelectItem value="file">File Link</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Description (optional)</Label>
                          <Input
                            value={documentForm.description}
                            onChange={(e) => setDocumentForm({ ...documentForm, description: e.target.value })}
                            placeholder="Brief description..."
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAddDocument(false)}>
                          Cancel
                        </Button>
                        <Button 
                          onClick={handleAddDocument}
                          disabled={addingDocument || !documentForm.name || !documentForm.url}
                        >
                          {addingDocument ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                          Add Document
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CaseManagement;
